import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import useEntries from '../Common/useEntries';
import EventCard from './EventCard';
import Pager from '../Common/Pager';
import { eventCategory, isNewsItem } from './eventFilters';
import {
    PageHero,
    Section,
    LoadingState,
    EmptyState,
    FilterBar,
    FilterLabel,
} from '../Common/PageLayout';

const PER_PAGE = 9;
const ALL = 'all';

const breadcrumbs = [{ label: 'News and press releases' }];

/**
 * Paginated archive of the Ministry's news items and press releases. On a directorate
 * subdomain, `directorateSlug` limits it to that directorate's news.
 */
export default function EventList({ directorateSlug } = {}) {
    const { items, loading } = useEntries({
        content_type: 'events',
        ...(directorateSlug ? { 'fields.directorate.slug': directorateSlug } : {}),
        order: '-sys.createdAt',
        limit: 400,
    });

    const [term, setTerm] = useState('');
    const [category, setCategory] = useState(ALL);
    const [year, setYear] = useState(ALL);
    const [page, setPage] = useState(1);

    const news = useMemo(() => items.filter(isNewsItem), [items]);

    const categories = useMemo(() => {
        const set = new Set();
        news.forEach((entry) => {
            const value = eventCategory(entry);
            if (value) set.add(value);
        });
        return Array.from(set).sort();
    }, [news]);

    const years = useMemo(() => {
        const set = new Set();
        news.forEach((entry) => {
            const raw = entry.fields?.date || entry.sys?.createdAt;
            const date = raw ? new Date(raw) : null;
            if (date && !Number.isNaN(date.getTime())) set.add(String(date.getFullYear()));
        });
        return Array.from(set).sort((a, b) => Number(b) - Number(a));
    }, [news]);

    const filtered = useMemo(() => {
        const needle = term.trim().toLowerCase();
        return news.filter((entry) => {
            const title = (entry.fields?.title || '').toLowerCase();
            if (needle && !title.includes(needle)) return false;
            if (category !== ALL && eventCategory(entry) !== category) return false;
            if (year !== ALL) {
                const raw = entry.fields?.date || entry.sys?.createdAt;
                const date = raw ? new Date(raw) : null;
                if (!date || Number.isNaN(date.getTime())) return false;
                if (String(date.getFullYear()) !== year) return false;
            }
            return true;
        });
    }, [news, term, category, year]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    const safePage = Math.min(page, totalPages);
    const visible = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

    const update = (setter) => (event) => {
        setter(event.target.value);
        setPage(1);
    };

    return (
        <>
            <Helmet>
                <title>News and press releases | Ministry of Finance, Timor-Leste</title>
                <meta
                    name="description"
                    content="Press releases, announcements and news published by the Ministry of Finance of the Democratic Republic of Timor-Leste."
                />
            </Helmet>

            <PageHero
                eyebrow="Newsroom"
                title="News and press releases"
                description="Official announcements, press releases and updates from the Ministry of Finance and its directorates."
                breadcrumbs={breadcrumbs}
            />

            <Section>
                <FilterBar resultCount={loading ? null : filtered.length} resultNoun="items">
                    <FilterLabel>Filter by</FilterLabel>

                    {categories.length > 0 && (
                        <TextField
                            select
                            id="news-category"
                            label="Category"
                            variant="outlined"
                            size="small"
                            value={category}
                            onChange={update(setCategory)}
                            style={{ minWidth: 180 }}
                        >
                            <MenuItem value={ALL}>All categories</MenuItem>
                            {categories.map((value) => (
                                <MenuItem key={value} value={value}>
                                    {value}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}

                    <TextField
                        select
                        id="news-year"
                        label="Year"
                        variant="outlined"
                        size="small"
                        value={year}
                        onChange={update(setYear)}
                        style={{ minWidth: 140 }}
                    >
                        <MenuItem value={ALL}>All years</MenuItem>
                        {years.map((value) => (
                            <MenuItem key={value} value={value}>
                                {value}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        id="news-search"
                        label="Search"
                        variant="outlined"
                        size="small"
                        placeholder="Search by title"
                        value={term}
                        onChange={update(setTerm)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="disabled" />
                                </InputAdornment>
                            ),
                        }}
                        style={{ minWidth: 260 }}
                    />
                </FilterBar>

                {loading && <LoadingState label="Loading news" />}

                {!loading && visible.length === 0 && (
                    <EmptyState
                        title="No news items match these filters"
                        description="Try clearing the category or year filter, or searching for a different term."
                    />
                )}

                {!loading && visible.length > 0 && (
                    <>
                        <Grid container spacing={3}>
                            {visible.map((entry) => (
                                <Grid item xs={12} sm={6} md={4} key={entry.sys.id}>
                                    <EventCard event={entry} />
                                </Grid>
                            ))}
                        </Grid>
                        <Pager page={safePage} totalPages={totalPages} onChange={setPage} />
                    </>
                )}
            </Section>
        </>
    );
}
